import { HamburgerMenuIcon } from '@radix-ui/react-icons'
import { useLayoutEffect, useRef, useState } from 'react'
import { ImperativePanelHandle } from 'react-resizable-panels'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { RenderTopMenuChildComponentProvider } from '@/providers/TopMenuChildrenProvider'

import { Button } from '@/components/ui/button'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import { Toaster } from '@/components/ui/toaster'
import { Sidebar } from '@/components/compliance-assistant/layout/Sidebar'
import LeftArrow from '@/assets/LeftArrow'

import { useRenderTopMenuChildComponent } from '@/providers/TopMenuChildrenProvider'

const MIN_SIZE_IN_PIXELS = 220
const WINDOW = 10
const MOBILE_WIDTH = 1024

function getMinSize(width: number) {
  return (MIN_SIZE_IN_PIXELS / width) * 100
}

export default function Layout() {
  const initialClientWidth = document.documentElement.clientWidth
  const lessThanMinWith = (currentWidth: number) => currentWidth < MOBILE_WIDTH
  const [minSize, setMinSize] = useState(getMinSize(initialClientWidth))
  const [lastWidth, setLastWidth] = useState(initialClientWidth)
  const [isCollapsed, setIsCollapsed] = useState(lessThanMinWith(initialClientWidth))
  const sidebarPanelRef = useRef<ImperativePanelHandle>(null)
  const currentParentRef = sidebarPanelRef.current

  const expandSidebar = () => {
    if (currentParentRef) {
      currentParentRef.isExpanded() ? currentParentRef.collapse() : currentParentRef.expand()
    }
  }

  useLayoutEffect(() => {
    const panelGroup = document.querySelector('[data-panel-group-id="group"]')
    const resizeHandles = document.querySelectorAll('[data-panel-resize-handle-id]')
    if (!panelGroup) return
    const observer = new ResizeObserver(() => {
      const currentParentRef = sidebarPanelRef.current
      if (!currentParentRef) return
      let currentWidth = panelGroup.clientWidth
      resizeHandles.forEach((resizeHandle) => {
        currentWidth -= resizeHandle.clientWidth
      })
      setMinSize(getMinSize(currentWidth))
      if (lastWidth >= MOBILE_WIDTH && lessThanMinWith(currentWidth)) {
        currentParentRef.collapse()
      }
      if (lastWidth < MOBILE_WIDTH && !lessThanMinWith(currentWidth)) {
        currentParentRef.expand()
      }
      setLastWidth(currentWidth)
    })
    observer.observe(panelGroup)
    resizeHandles.forEach((resizeHandle) => {
      observer.observe(resizeHandle)
    })

    return () => {
      observer.disconnect()
    }
  }, [currentParentRef, lastWidth, minSize])

  return (
    <RenderTopMenuChildComponentProvider>
      <ResizablePanelGroup
        className="fixed h-screen w-screen"
        direction="horizontal"
        id="group"
        style={{ height: '100%' }}
      >
        <ResizablePanel
          className="flex h-screen w-screen max-w-[220px]"
          collapsible
          defaultSize={minSize}
          id="sidebar-panel"
          minSize={minSize}
          onCollapse={() => setIsCollapsed(true)}
          onExpand={() => !lessThanMinWith(lastWidth) && setIsCollapsed(false)}
          ref={sidebarPanelRef}
        >
          <Sidebar />
        </ResizablePanel>
        <ResizableHandle id="horizontal-handle" />
        <Toaster />
        <ResizablePanel
          defaultSize={100 - minSize}
          id="vertical-panel"
          minSize={100 - minSize - WINDOW}
          className="content-container"
        >
          <div className="relative h-full">
            <div className="fixed top-0 w-full z-10 bg-white border-b border-gray-200">
              <TopMenu expandSidebar={expandSidebar} isCollapsed={isCollapsed} />
            </div>

            <div className="content-area overflow-auto h-[calc(100%-64px)] mt-16">
              <Outlet />
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </RenderTopMenuChildComponentProvider>
  )
}

const routesWithBackButton = [
  '/procesamientos/crear',
  '/procesamientos/editar',
  '/procesamientos/ver',
]

export function TopMenu({
  expandSidebar,
  isCollapsed,
}: {
  expandSidebar: () => void
  isCollapsed: boolean
}) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { topMenuChildComponent } = useRenderTopMenuChildComponent()
  return (
    <div
      className={`fixed flex w-full ${
        topMenuChildComponent ? 'min-h-[1rem] h-auto' : 'h-16'
      } items-center border-b border-gray-200`}
    >
      {routesWithBackButton.includes(pathname) && !isCollapsed && (
        <Button
          className="opacity-70 hover:opacity-100 hover:bg-inherit ml-8 px-0"
          onClick={() => navigate(-1)}
          variant="ghost"
        >
          <span className="flex items-center">
            <LeftArrow className="w-5 h-5" />
            <span className="ml-2">Volver</span>
          </span>
        </Button>
      )}

      {isCollapsed && (
        <Button
          className="opacity-70 hover:opacity-100 hover:bg-inherit ml-6"
          onClick={expandSidebar}
          size="icon"
          variant="ghost"
        >
          <HamburgerMenuIcon />
        </Button>
      )}
      {topMenuChildComponent}
    </div>
  )
}
